import { FuelIcon, FuelTag, FuelTypography } from '@frete.com/fuel-react'
import { useEffect, useState } from 'react'
import { DropDownMenu } from '~components'
import { OptionProps, Props } from './interfaces'
import * as Styled from './styled'
import { useField } from 'formik'

function Select({
  options,
  name,
  holder,
  hint,
  label,
  onChange,
  state,
  externalError,
  withRadios = false,
  defaultValue,
  ...props
}: Props) {
  const initialDisplayValue: string =
    typeof holder === 'string' || typeof holder === 'number' ? String(holder) : defaultValue ? String(defaultValue) : ''

  const [displayValue, setDisplayValue] = useState(holder)
  const [isOptionsOpened, setOptionsOpened] = useState(false)
  const [field, { touched, error }] = useField({ name, ...props })

  function openOptions() {
    if (!props.disabled) {
      setOptionsOpened(true)
    }
  }

  function closeOptions() {
    setOptionsOpened(false)
  }

  function handleChange(option: OptionProps) {
    closeOptions()
    onChange?.(option.id)
    field.onChange({ target: { name, value: option.id } })
  }

  useEffect(() => {
    if (field.value != null && field.value !== '') {
      const option = options.find((item) => item.id === field.value)
      if (option) setDisplayValue(String(option.name))
    } else if (defaultValue) {
      const option = options.find((item) => item.id === defaultValue)
      if (option) setDisplayValue(String(option.name))
    } else {
      setDisplayValue(initialDisplayValue)
    }
  }, [field.value, options, initialDisplayValue, defaultValue])

  return (
    <Styled.Wrapper>
      <Styled.Label htmlFor={name} $state={(touched && error) || externalError ? 'danger' : state}>
        {label}
      </Styled.Label>
      <Styled.Select
        aria-label={label}
        $disabled={props.disabled}
        $state={(touched && error) || externalError ? 'danger' : state}
        role="combobox"
        onClick={openOptions}
        holder={holder}
        itemSelected={field.value != null && field.value !== ''}
      >
        <Styled.Input {...props} name={name} aria-hidden={true} placeholder={`custom-select-${name}`} />
        {displayValue}

        <Styled.Icon $disabled={props.disabled}>
          <FuelIcon name="chevron-down" size="tiny" />
        </Styled.Icon>
      </Styled.Select>
      <DropDownMenu
        width="100%"
        isOpened={isOptionsOpened}
        onClickOutside={closeOptions}
        options={options}
        onChange={handleChange}
        renderOption={(option) => (
          <Styled.Option key={option.id}>
            {withRadios && (
              <Styled.Radio
                name={name}
                value={option.id as string}
                onChange={() => handleChange(option)}
                checked={field.value === option.id}
              />
            )}
            <div>
              {option.description ? (
                <Styled.AccessOption>
                  {option.name === 'Colaborador com permissão de compra' ? (
                    <Styled.DescContent>
                      <FuelTypography color="neutral-strong" weight="bold" size="body-md">
                        {option.name}
                      </FuelTypography>
                      <FuelTag label="Novo" color="blue" />
                    </Styled.DescContent>
                  ) : (
                    <FuelTypography color="neutral-strong" weight="bold" size="body-md">
                      {option.name}
                    </FuelTypography>
                  )}
                  <FuelTypography color="neutral-default" weight="medium" size="body-sm" spacingTop={4}>
                    {option.description}
                  </FuelTypography>
                </Styled.AccessOption>
              ) : (
                <FuelTypography color="neutral-default" weight="medium" size="body-sm" spacingTop={4}>
                  {option.name}
                </FuelTypography>
              )}
            </div>
          </Styled.Option>
        )}
      />
      {((touched && error) || externalError) && <Styled.Error tag="p">{error || externalError}</Styled.Error>}
      {hint && <Styled.Message>{hint}</Styled.Message>}
    </Styled.Wrapper>
  )
}

export default Select
